import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'clsx';
import Yup from '../../utils/yup';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/react-hooks';
import {
  Grid,
  Stepper,
  Step,
  StepLabel,
  Icon,
  Typography,
  Button,
  Container,
} from '@material-ui/core';
import { Formik, Form } from 'formik';
import { useSnackbar } from 'notistack';

import Logo from '../../images/brand/CoreCapital/CoreCapital_SAFI_.png';
import useStyles from './styles';
import StepOneForm from './StepOneForm';
import StepTwoForm from './StepTwoForm';
import StepThreeForm from './StepThreeForm';
import { successMessage, errorMessage } from '../../utils/commonFunctions';
import { SUBMIT_RAISING_PARTICIPATION, GET_FUND } from './queries';
import StepFourForm from './StepFourForm';

function CustomStepIcon(props) {
  const { children, active, completed } = props;
  const styles = useStyles();

  const classes = cx(
    styles.icon,
    active || completed ? styles.active : styles.inactive,
  );

  return (
    <Icon className={classes}>
      <Typography variant="inherit">{children}</Typography>
    </Icon>
  );
}

CustomStepIcon.propTypes = {
  children: PropTypes.node.isRequired,
  active: PropTypes.bool.isRequired,
  completed: PropTypes.bool.isRequired,
};

const STEP_PERSONAL_DATA = 3;
const FINAL_STEP = 4;

function CognitoForm(props) {
  const steps = ['C', 'O', 'R', 'E'];
  const { raising } = props;
  const { fund } = raising;

  const styles = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [userID, setUserID] = useState(
    JSON.parse(localStorage.getItem('user')).username,
  );
  const [activeStep, setActiveStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [isButtonDisabled, setButtonDisabled] = useState(false);
  const [mainParticipant, setMainParticipant] = useState({
    documentNumber: '',
    clientID: '',
    clientName: '',
    participationPercentage: 0,
    isRepresentative: true,
    communicationCellphone: '',
    correoIntermediario: '',
    mainUser: [], // Accept only verified users
  });

  const formInitialValues = {
    participationAmountOrFee: 'fee',
    investmentValue: 1,
    feeCount: 0,
    riskExchange: false,
    hasCoParticipation: false,
    hasLinkedPeople: false,
    participationPercentage: 100,
    isRepresentative: true,
    communicationCellphone: '',
    correoIntermediario: '',
    coparticipants: [],
    mainUser: [],
    classFund: '',
    showClass: true,
    errors: '',
  };

  const { data: infoFund } = useQuery(GET_FUND, {
    variables: { id: fund.id },
  });

  const validationSchema = Yup.object().shape({
    clientID: Yup.string().required(),
    clientName: Yup.string().required(),
    documentNumber: Yup.string().required(),
    showClass: Yup.boolean(),
    classFund: Yup.string().when('showClass', showClass => {
      if (showClass && fund.hasClasses)
        return Yup.string().required('Debe elegir una clase');
    }),
    participationPercentage: Yup.number().required(),
    isRepresentative: Yup.bool().required(),
    communicationCellphone: Yup.string().required(),
    correoIntermediario: Yup.string().required(),
    participationAmountOrFee: Yup.string().required(),
    investmentValue: Yup.number()
      .positive('Ingrese un valor positivo mayor a cero')
      .required(),
    riskExchange: Yup.bool().required(),
    hasCoParticipation: Yup.bool().required(),
    hasLinkedPeople: Yup.bool().required(),
  });

  const [submitData] = useMutation(SUBMIT_RAISING_PARTICIPATION, {
    onError: error => {
      if (error.message.match('duplicate')) {
        errorMessage(
          'El participante ya es parte de este levantamiento en la rueda activa.',
          enqueueSnackbar,
        );
      } else {
        error.graphQLErrors.map(function({ message }, i) {
          errorMessage(
            'Hubo un error, refresque e intente de nuevo por favor. ' + message,
            enqueueSnackbar,
          );
        });
      }
    },
    onCompleted: ({ createParticipation }) => {
      if (createParticipation.created) {
        successMessage('Participación creada con éxito.', enqueueSnackbar, {
          callback: () => setSubmitted(true),
        });
        setActiveStep(activeStep + 1);
      }
    },
  });

  const getCurrentStep = participants => {
    switch (activeStep) {
      case 0:
        return <StepOneForm raising={raising} />;
      case 1:
        return (
          <StepTwoForm raising={raising} data={participants} userID={userID} />
        );
      case 2:
        return <StepThreeForm raising={raising} data={participants} />;
      case 3:
        return <StepFourForm raising={raising} />;
      default:
        return <></>;
    }
  };

  const getFundType = () => {
    switch (fund.fundType) {
      case 'PR':
        return 'Privada';
      case 'PB':
        return 'Pública';
      default:
        return '';
    }
  };

  const getStepTitle = () => {
    switch (activeStep) {
      case 0:
        return (
          <>
            <b> {fund.businessName} </b> <br></br>
            <b className={styles.stepLabel}>Paso 1:</b> Información de la Oferta{' '}
            {getFundType()}
          </>
        );
      case 1:
        return (
          <>
            <b> {fund.businessName} </b> <br></br>
            <b className={styles.stepLabel}>Paso 2:</b> Información del
            partícipe
          </>
        );
      case 2:
        return (
          <>
            <b> {fund.businessName} </b> <br></br>
            <b className={styles.stepLabel}>Paso 3:</b> Datos sobre la
            participación
          </>
        );
      case 3:
        return (
          <>
            <b> {fund.businessName} </b> <br></br>
            <b className={styles.stepLabel}>Paso 4:</b> Confirmación de los
            datos
          </>
        );
      case 4:
        return (
          <>
            Muchas gracias por realizar su registro para el aumento de capitales
            del <b className={styles.stepLabel}> {fund.businessName}</b>, nos
            contactaremos con usted para comunicarle el resultado de la
            adjudicación.
          </>
        );
      default:
        return '';
    }
  };

  const nextStep = form => {
    const { values, setFieldValue } = form;
    if (activeStep === 0) {
      setActiveStep(activeStep + 1);
    }
    if (activeStep === 1) {
      const participantData = {
        documentType: values.documentType,
        documentNumber: values.documentNumber,
        clientID: values.clientID,
        clientName: values.clientName,
        communicationCellphone: values.communicationCellphone,
        correoIntermediario: values.correoIntermediario,
        participationPercentage: 100,
        isRepresentative: true,
      };
      setMainParticipant(participantData);
      setFieldValue(
        'coparticipants',
        form.values.coparticipants.length
          ? form.values.coparticipants
          : [participantData],
      );
      delete form.errors.classFund;
      if (Object.keys(form.errors).length === 0) {
        setActiveStep(activeStep + 1);
      }
    }
    if (activeStep === 2) {
      if (infoFund?.fund.hasClasses) {
        if (form.errors.classFund) {
          setActiveStep(activeStep);
        } else {
          setActiveStep(activeStep + 1);
        }
      } else {
        setActiveStep(activeStep + 1);
      }
    }
  };

  const prevStep = form => {
    if (form.values.errors.length > 1) form.values.errors = '';
    const { values, setFieldValue } = form;
    if (activeStep === 1) {
      const participantData = {
        documentType: values.documentType,
        documentNumber: values.documentNumber,
        userID: values.userID,
        clientID: values.clientID,
        clientName: values.clientName,
        communicationCellphone: values.communicationCellphone,
        correoIntermediario: values.correoIntermediario,
        participationPercentage: 100,
        isRepresentative: true,
      };
      setMainParticipant(participantData);
      setFieldValue(
        'coparticipants',
        form.values.coparticipants.length
          ? form.values.coparticipants
          : [participantData],
      );
      delete form.errors.classFund;
    }
    if (activeStep === 2) {
      Object.keys(mainParticipant).map(k =>
        form.setFieldValue(k, mainParticipant[k]),
      );
    }
    form.setErrors({});
    setActiveStep(activeStep - 1);
  };

  const onSubmit = values => {
    setButtonDisabled(true);
    const participants = values.coparticipants.map(p => ({
      clientId: p.clientID,
      participationPercentage: Number(p.participationPercentage),
      isRepresentative: p.isRepresentative,
      communicationCellphone: p.communicationCellphone,
      correoIntermediario: p.correoIntermediario,
    }));
    submitData({
      variables: {
        raisingId: raising.id,
        input: {
          feeNumber: values.feeCount,
          isRiskExchange: values.riskExchange,
          participants,
          classFund: values.classFund,
          hasLinkedPeople: values.hasLinkedPeople,
          createdBy: userID,
        },
      },
    });
  };

  return (
    <Grid container classes={{ root: styles.container }} justify="center">
      <Grid container item className={styles.header} justify="center">
        <img src={Logo} alt="Logo Dorado" className={styles.image} />
      </Grid>
      <Grid container className={styles.steps}>
        <Stepper
          activeStep={activeStep}
          className={styles.stepper}
          connector={null}
        >
          {steps.map(label => (
            <Step key={label} classes={{ horizontal: styles.horizontal }}>
              <StepLabel
                classes={{
                  active: styles.active,
                  completed: styles.completed,
                  root: styles.icon,
                }}
                StepIconComponent={CustomStepIcon}
                StepIconProps={{ children: label }}
              />
            </Step>
          ))}
        </Stepper>
        <Formik
          initialValues={formInitialValues}
          validationSchema={validationSchema}
          validateOnChange
          validateOnBlur
        >
          {form => {
            return (
              <Container>
                <Typography align="center">{getStepTitle()}</Typography>
                <Form>
                  {getCurrentStep(form.values.coparticipants)}
                  <Grid
                    container
                    x1={12}
                    direction="row"
                    className={styles.buttons}
                  >
                    <Grid container item xs={6} justify="flex-start">
                      {activeStep > 0 && activeStep !== FINAL_STEP ? (
                        <Button
                          color="primary"
                          variant="contained"
                          disabled={submitted}
                          onClick={() => prevStep(form)}
                        >
                          Volver
                        </Button>
                      ) : null}
                    </Grid>
                    <Grid container item xs={6} justify="flex-end">
                      {activeStep === STEP_PERSONAL_DATA ? (
                        <Button
                          color="secondary"
                          variant="contained"
                          disabled={isButtonDisabled}
                          onClick={e => {
                            onSubmit(form.values);
                          }}
                        >
                          Subir
                        </Button>
                      ) : activeStep < FINAL_STEP ? (
                        <Button
                          color="secondary"
                          variant="contained"
                          onClick={() => nextStep(form)}
                          disabled={form.values.errors.length > 0}
                        >
                          Siguiente
                        </Button>
                      ) : null}
                    </Grid>
                  </Grid>
                </Form>
              </Container>
            );
          }}
        </Formik>
      </Grid>
    </Grid>
  );
}

CognitoForm.propTypes = {
  raising: PropTypes.object.isRequired,
};

export default CognitoForm;
